/**
 * QrSettlementAgGridHook
 *
 * AG Grid hook for the QR Settlements LiveView.
 *
 * Displays settled QR transactions fetched from the transactions table.
 * Columns are limited to those that actually exist in the transactions table.
 *
 * Lifecycle
 *  mounted()   – Creates the grid with QR-specific column definitions.
 *  destroyed() – Destroys the grid API to free memory.
 *
 * LiveView events received (via handleEvent):
 *  "update_qr_settlements_data" – { rows: [...] } – pushed by the server after filter change.
 *
 * LiveView events pushed (via pushEvent):
 *  "qr_settlement_row_clicked" – { id: <integer> } – when a row is selected.
 */

const QrSettlementAgGridHook = {
  mounted() {
    const container = this.el;
    const rows = JSON.parse(container.dataset.rows || "[]");

    const SETTLED_COLOR   = "#16A34A";
    const DEFAULT_BADGE_BG = "#9CA3AF";

    const createBadge = (value, color) => {
      if (!value) return "";
      return `<span class="ag-badge" style="background:${color || DEFAULT_BADGE_BG}">${value}</span>`;
    };

    const settleBadge = (p) =>
      createBadge(p.value, p.value === "settled" ? SETTLED_COLOR : DEFAULT_BADGE_BG);

    const amountCell = (params) => {
      if (params.value === null || params.value === undefined || params.value === "") return "";
      const n = parseFloat(params.value);
      return isNaN(n)
        ? params.value
        : n.toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const columnDefs = [
      { headerName: "#",                  field: "id",                    width: 100, pinned: "left", sortable: true, filter: false },
      { headerName: "Transaction ID",     field: "transaction_id",        width: 200 },
      { headerName: "Merchant Tag (MID)", field: "merchant_tag",          width: 180 },
      { headerName: "Merchant ID",        field: "merchant_id",           width: 150 },
      { headerName: "Bank User ID",       field: "bank_user_id",          width: 150 },
      { headerName: "Device ID (TID)",    field: "device_id",             width: 180 },
      { headerName: "Acquirer/Switch TID", field: "ysp_tid",               width: 160 },
      { headerName: "Batch Number",       field: "batch_number",          width: 160 },
      { headerName: "Settlement ID",      field: "settlement_id",         width: 200 },
      { headerName: "Settle Status",      field: "settlement_status",     width: 130, pinned: "right", cellRenderer: settleBadge },
      { headerName: "Txn Status",         field: "status",                width: 120 },
      { headerName: "Settled At",         field: "settlement_date_time",  width: 190, sort: "desc" },
      { headerName: "Amount",             field: "transaction_amount",    width: 130, cellRenderer: amountCell, type: "numericColumn" },
      { headerName: "UHID",               field: "uhid",                  width: 150 },
      { headerName: "Inserted At",        field: "inserted_at",           width: 190 },
    ];

    const gridOptions = {
      columnDefs,
      rowData: rows,
      pagination: true,
      paginationPageSize: 10,
      paginationPageSizeSelector: [10, 20, 50, 100],
      popupParent: document.body,
      defaultColDef: {
        resizable: true,
        sortable: true,
        filter: true,
        floatingFilter: true,
        minWidth: 70,
      },
      animateRows: true,
      rowSelection: "single",
      sideBar: {
        toolPanels: [
          {
            id: "columns",
            labelDefault: "Columns",
            labelKey: "columns",
            iconKey: "columns",
            toolPanel: "agColumnsToolPanel",
            toolPanelParams: {
              suppressRowGroups: true,
              suppressValues: true,
              suppressPivots: true,
              suppressPivotMode: true,
              suppressColumnFilter: false,
              suppressColumnSelectAll: false,
              suppressColumnExpandAll: false,
            },
          },
        ],
      },
      onRowClicked: (event) => {
        if (event.data && event.data.id !== undefined) {
          this.pushEvent("qr_settlement_row_clicked", { id: event.data.id });
        }
      },
      overlayNoRowsTemplate:
        `<span class="ag-no-rows-overlay">No settled QR transactions found. Adjust filters or check settlement processing status.</span>`,
    };

    if (window.agGrid && window.agGrid.createGrid) {
      this.gridApi = window.agGrid.createGrid(container, gridOptions);
    } else {
      console.error("QrSettlementAgGridHook: agGrid not found on window");
    }

    this.handleEvent("update_qr_settlements_data", ({ rows: newRows }) => {
      if (this.gridApi) {
        this.gridApi.setGridOption("rowData", newRows || []);
      }
    });
  },

  destroyed() {
    if (this.gridApi) {
      this.gridApi.destroy();
      this.gridApi = null;
    }
  },
};

export default QrSettlementAgGridHook;
